// PACKAGE #15 — Seller Ratings & Reviews.
// Phase B.1 built the read-only shell, modeled directly on
// ../FollowList.js's modal (backdrop/scroll-lock/Escape/close animation).
// Phase B.2 adds the write flow on top: an eligibility fetch that decides
// which CTA (if any) to show, and ReviewFormSheet for create/edit/delete.
// The public GET /api/reviews/:sellerId list itself is still what renders
// the cards — the eligibility call is a separate, self-only lookup.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ReviewsList.scss';
import ReportsModal from '../../Reports/Reports';
import ReviewFormSheet from './ReviewFormSheet';
import { getAccessToken } from '../../../awsAuth';

const DEFAULT_AVATAR =
  'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.location.hostname.includes('onrender.com')
    ? 'https://cvanto-api.onrender.com'
    : 'http://localhost:5000');

const API = process.env.REACT_APP_API || API_BASE;

const apiUrl = (path) => {
  if (!path) return API;
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(API || '').replace(/\/+$/, '');
  const p = String(path || '');
  return p.startsWith('/') ? `${base}${p}` : `${base}/${p}`;
};

const PAGE_LIMIT = 20;

// ---- pure helpers (exported for unit tests — no component-render test
// pattern exists in this repo yet, see AdsCabinet.test.js/AdSlot.test.js
// for the established precedent) --------------------------------------

export function formatReviewerName(reviewer) {
  const name = reviewer?.username;
  return name && String(name).trim() ? name : 'Користувач';
}

// Cursor-paginated pages should never overlap in practice, but a reviewer
// editing their review between page loads could in theory resurface the
// same reviewId across two fetches — dedupe defensively rather than trust
// that never happens, so "Завантажити ще" can never render a duplicate card.
export function mergeReviewPages(existingItems, newItems) {
  const seen = new Set((existingItems || []).map((it) => it.reviewId));
  const appended = (newItems || []).filter((it) => !seen.has(it.reviewId));
  return [...(existingItems || []), ...appended];
}

// The report system's targetId for a review must be exactly reviewId
// (server/services/reviewService.js's parseReviewId resolves it via a
// single GetItem) — never reviewerId, never a synthesized id.
export function buildReviewReportTarget(review) {
  return { targetType: 'review', targetId: review?.reviewId };
}

export function formatReviewDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}

// ---- Phase B.2: CTA/eligibility presentation (pure — mirrors the exact
// {eligible, mode, reason} contract of GET /api/reviews/:sellerId/eligibility) --

export function getCtaLabel(mode) {
  if (mode === 'edit') return 'Редагувати свій відгук';
  if (mode === 'create') return 'Залишити відгук';
  return null;
}

// SELF and an unrecognized/absent reason both render nothing — SELF never
// reaches the UI in practice (the component skips the eligibility fetch
// entirely for the profile owner), but the mapping stays total/defensive
// rather than assuming that.
export function getIneligibleMessage(reason) {
  if (reason === 'HIDDEN') return 'Цей відгук приховано модерацією.';
  if (reason === 'NO_THREAD' || reason === 'NOT_TWO_WAY') {
    return 'Залишити відгук можна після двостороннього спілкування з продавцем.';
  }
  return null;
}

export function isOwnReview(review, currentUserId) {
  return !!currentUserId && String(review?.reviewerId) === String(currentUserId);
}

const Stars = ({ rating }) => {
  const r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  return (
    <span className="rv-stars" aria-label={`Оцінка ${r} з 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={`rv-star ${i < r ? 'is-filled' : ''}`} aria-hidden="true">★</span>
      ))}
    </span>
  );
};

const ReviewsList = ({ sellerId, currentUserId, isOwner, onClose, onAggregateChange }) => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);

  // Phase B.2 — write flow
  const [eligibility, setEligibility] = useState(null); // { eligible, mode, reason, myReview } | null
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [formState, setFormState] = useState(null); // { mode: 'create'|'edit', initialReview } | null

  const scrollYRef = useRef(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    setMounted(true);
    scrollYRef.current = window.scrollY || 0;
    const body = document.body;
    const html = document.documentElement;
    body.style.position = 'fixed';
    body.style.top = `-${scrollYRef.current}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';

    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overscrollBehavior = '';
      window.scrollTo(0, scrollYRef.current);
    };
  }, []);

  const handleRequestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => onClose?.(), 220);
  }, [isClosing, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleRequestClose();
    };
    window.addEventListener('keydown', onKey, { passive: true });
    return () => window.removeEventListener('keydown', onKey);
  }, [handleRequestClose]);

  const loadFirstPage = useCallback(async () => {
    if (!sellerId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(apiUrl(`/api/reviews/${encodeURIComponent(sellerId)}?limit=${PAGE_LIMIT}`));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (cancelledRef.current) return;
      setItems(Array.isArray(data.items) ? data.items : []);
      setNextCursor(data.nextCursor || null);
    } catch {
      if (!cancelledRef.current) setError(true);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => {
    cancelledRef.current = false;
    loadFirstPage();
    return () => {
      cancelledRef.current = true;
    };
  }, [loadFirstPage]);

  // Skipped entirely for the profile owner (their own "can I review
  // myself" answer is always SELF/none, not worth a request) and for an
  // anonymous viewer (the endpoint requires auth). Non-owner + logged-in
  // is the only case where the CTA area has anything to show.
  const loadEligibility = useCallback(async () => {
    if (!sellerId || isOwner || !currentUserId) {
      setEligibility(null);
      return;
    }
    setEligibilityLoading(true);
    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(apiUrl(`/api/reviews/${encodeURIComponent(sellerId)}/eligibility`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!cancelledRef.current) setEligibility(data);
    } catch {
      if (!cancelledRef.current) setEligibility(null);
    } finally {
      if (!cancelledRef.current) setEligibilityLoading(false);
    }
  }, [sellerId, isOwner, currentUserId]);

  useEffect(() => {
    loadEligibility();
  }, [loadEligibility]);

  // After any successful write (create/edit/delete/reactivate): the
  // aggregate (Profile.js's sellerRating), the review list, and the
  // eligibility/mode all need to reflect the new state. Three requests
  // total, fired once per completed action — not per keystroke, not
  // polled — so this is not a request storm.
  const refreshAfterWrite = useCallback(async () => {
    await Promise.all([loadFirstPage(), loadEligibility()]);
    onAggregateChange?.();
  }, [loadFirstPage, loadEligibility, onAggregateChange]);

  const openCreateForm = () => setFormState({ mode: 'create', initialReview: null });
  const openEditForm = (initialReview) => setFormState({ mode: 'edit', initialReview: initialReview || null });

  const handleLoadMore = async () => {
    if (!sellerId || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetch(
        apiUrl(`/api/reviews/${encodeURIComponent(sellerId)}?limit=${PAGE_LIMIT}&cursor=${encodeURIComponent(nextCursor)}`)
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems((prev) => mergeReviewPages(prev, Array.isArray(data.items) ? data.items : []));
      setNextCursor(data.nextCursor || null);
    } catch {
      // Load-more failure is non-fatal — the already-loaded page stays
      // usable, the button just stays available to retry.
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div
      className="reviews-modal"
      data-mounted={mounted ? '1' : '0'}
      data-state={isClosing ? 'closing' : 'open'}
      onClick={handleRequestClose}
      role="dialog"
      aria-modal="true"
      aria-label="Відгуки"
    >
      <div
        className="reviews-modal__content"
        onClick={(e) => e.stopPropagation()}
        data-mounted={mounted ? '1' : '0'}
        data-state={isClosing ? 'closing' : 'open'}
      >
        <header className="reviews-modal__header">
          <div className="rm-bar">
            <h3 className="rm-title">Відгуки</h3>
            <button className="rm-close" type="button" aria-label="Закрити" onClick={handleRequestClose}>
              <span aria-hidden>×</span>
            </button>
          </div>
        </header>

        {!isOwner && currentUserId && !eligibilityLoading && (
          <div className="rv-cta-area">
            {(eligibility?.mode === 'create' || eligibility?.mode === 'edit') ? (
              <button
                type="button"
                className="rv-cta"
                onClick={() =>
                  eligibility.mode === 'edit'
                    ? openEditForm(eligibility.myReview)
                    : openCreateForm()
                }
              >
                {getCtaLabel(eligibility.mode)}
              </button>
            ) : (
              getIneligibleMessage(eligibility?.reason) && (
                <div className="rv-ineligible">{getIneligibleMessage(eligibility.reason)}</div>
              )
            )}
          </div>
        )}

        <div className="reviews-modal__body">
          {loading ? (
            <ul className="rv-skeleton">
              {Array.from({ length: 4 }).map((_, i) => (
                <li key={i} className="sk-row" style={{ animationDelay: `${0.02 * i + 0.04}s` }}>
                  <span className="sk-avatar" />
                  <div className="sk-lines">
                    <span className="sk-line sk-line--short" />
                    <span className="sk-line" />
                  </div>
                </li>
              ))}
            </ul>
          ) : error ? (
            <div className="rv-empty">
              <p>Не вдалося завантажити відгуки.</p>
              <button type="button" className="rv-retry" onClick={loadFirstPage}>Спробувати ще раз</button>
            </div>
          ) : items.length === 0 ? (
            <div className="rv-empty">
              <p>Ще немає відгуків.</p>
            </div>
          ) : (
            <>
              <ul className="rv-list">
                {items.map((review) => {
                  const own = isOwnReview(review, currentUserId);
                  return (
                    <li key={review.reviewId} className={`rv-item ${own ? 'rv-item--own' : ''}`}>
                      <button
                        className="rv-author"
                        type="button"
                        onClick={() => {
                          handleRequestClose();
                          navigate(`/profile/${review.reviewerId}`);
                        }}
                      >
                        <img src={review.reviewer?.avatar || DEFAULT_AVATAR} alt="" />
                        <span className="rv-author__name">
                          {formatReviewerName(review.reviewer)}
                          {own && <span className="rv-own-badge">Ваш відгук</span>}
                        </span>
                      </button>

                      <div className="rv-item__meta">
                        <Stars rating={review.rating} />
                        <span className="rv-date">{formatReviewDate(review.createdAt)}</span>
                      </div>

                      {review.text && <p className="rv-text">{review.text}</p>}

                      {own ? (
                        <button
                          type="button"
                          className="rv-edit-own"
                          onClick={() => openEditForm({ rating: review.rating, text: review.text })}
                        >
                          Редагувати
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="rv-report"
                          onClick={() => setReportTarget(buildReviewReportTarget(review))}
                        >
                          Поскаржитись
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>

              {nextCursor && (
                <button type="button" className="rv-load-more" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? 'Завантаження…' : 'Завантажити ще'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {reportTarget && (
        <ReportsModal
          open={!!reportTarget}
          onClose={() => setReportTarget(null)}
          targetType={reportTarget.targetType}
          targetId={reportTarget.targetId}
          userId={currentUserId}
        />
      )}

      {formState && (
        <ReviewFormSheet
          open={!!formState}
          onClose={() => setFormState(null)}
          sellerId={sellerId}
          mode={formState.mode}
          initialReview={formState.initialReview}
          onSaved={refreshAfterWrite}
          onDeleted={refreshAfterWrite}
        />
      )}
    </div>
  );
};

export default ReviewsList;
